import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Lightbulb, Mic, Pencil, FileEdit, MessageCircle, Mail, Scan, Flame, Footprints, Camera, Heart, Trophy, UserPlus, Brain, Apple, Music, Cigarette, Dribbble, Github, Bell, Globe, Sparkles, Loader2, X } from "lucide-react";
import { openInExternalBrowser } from "@/lib/platform";
import { supabase } from "@/integrations/supabase/client";
import { PreAuthMetric, incrementRateLimit, getDeviceId } from "@/lib/preAuthMetrics";
import { getAllMetrics } from "@/lib/metricDefinitions";
import { SafeHCaptcha } from "@/components/SafeHCaptcha";
import { toast } from "sonner";

const HCAPTCHA_SITE_KEY = "eebe1a6c-3fc4-42f4-9df4-6bee9822d538";

interface OnboardingTutorialProps {
  onComplete: () => void;
  isTourFlow?: boolean; // If true, show "Start tracking now" instead of "Get started"
  metricsGenerated?: boolean; // If true, show "Continue to sign up" instead of other text
  metricsGenerating?: boolean; // If true, show loading overlay for metrics generation
  userInput?: string; // User input for metrics generation
  onMetricsGenerated?: (metrics: PreAuthMetric[]) => void; // Callback when metrics are generated
  onMetricsError?: () => void; // Callback when metrics generation fails
  metricsCount?: number; // Number of metrics generated
}

interface GeneratedMetric {
  key: string;
  name: string;
  emoji: string;
  unit: string;
  type: "numeric" | "boolean";
  goal?: number;
  value: number;
  inverse: boolean;
  isAdditive?: boolean;
  isRelative?: boolean;
}

// Helper to generate tutorial pages with translations
const getTutorialPages = (t: any) => [
  {
    title: t('tutorial_slides.p1_title'),
    subtext: t('tutorial_slides.p1_sub'),
    image: "/iphone-screenshots/dashboard-aaron.png",
    type: "single-image" as const,
    elements: [
      { text: t('tutorial_slides.p1_float1'), icon: Heart, color: "text-red-500", position: "top-[20%] -right-8" },
      { text: t('tutorial_slides.p1_float2'), icon: UserPlus, color: "text-blue-400", position: "top-[10%] -left-4" }
    ]
  },
  {
    title: t('tutorial_slides.p2_title'),
    subtext: t('tutorial_slides.p2_sub'),
    image: "/iphone-screenshots/run-track-nooverlay.png",
    overlayImage: "/iphone-screenshots/overlays-only.png",
    type: "scan-effect" as const,
    showIcon: "mic" as const,
  },
  {
    title: t('tutorial_slides.p3_title'),
    subtext: "accuracy-bullets", // Special marker for custom rendering
    image: "/iphone-screenshots/image-before.png",
    type: "single-image" as const,
    showIcon: "fileEdit" as const,
    adjustImagePosition: -20, // Move image up by 20px
  },
  {
    title: <span><span className="font-thin opacity-60">{t('tutorial_slides.p4_pre')}</span><span className="font-bold">{t('tutorial_slides.p4_bold')}</span><span className="font-thin opacity-60">{t('tutorial_slides.p4_post')}</span></span>,
    subtext: "🚬 🍎 🎵",
    image: "/iphone-screenshots/ai-text-entry.png",
    overlayImage: "/iphone-screenshots/ai-text-entry-overlays.png",
    type: "fade-overlay" as const,
  },
  {
    title: <span><span className="font-thin opacity-60">{t('tutorial_slides.p5_pre')}</span><span className="font-bold">{t('tutorial_slides.p5_bold1')}</span><span className="font-thin opacity-60">{t('tutorial_slides.p5_mid')}</span><span className="font-bold">{t('tutorial_slides.p5_bold2')}</span></span>,
    image: "/iphone-screenshots/confirm-values.png",
    type: "single-image" as const,
    showAiTip: true,
    aiTipText: t('tutorial_slides.p5_tip'),
  },
  {
    title: <span>{t('tutorial_slides.p6_pre')}<span className="relative inline-block"><span className="absolute inset-0 bg-gradient-to-r from-orange-200 to-fuchsia-500 blur-md opacity-60" /><span className="relative">{t('tutorial_slides.p6_bold')}</span></span>{t('tutorial_slides.p6_post')}</span>,
    subtext: null, // Dynamic cycling subtext
    image: "/iphone-screenshots/progress-snaps.png",
    type: "wide-image" as const,
  },
  {
    title: t('tutorial_slides.p7_title'),
    subtext: t('tutorial_slides.p7_sub'),
    image: "/iphone-screenshots/run-results.png",
    type: "single-image" as const,
    showIcon: "edit" as const,
    elements: [
      { text: t('tutorial_slides.p7_float1'), icon: Footprints, color: "text-emerald-400", position: "top-[15%] -left-6" },
      { text: t('tutorial_slides.p7_float2'), icon: Brain, color: "text-purple-400", position: "top-[22%] -right-6", delay: 1.25 }
    ],
  },
  {
    title: t('tutorial_slides.p8_title'),
    type: "bullet-list" as const,
    bullets: [
      { emoji: "🔥", text: <span><span className="font-bold">{t('tutorial_slides.p8_b1_bold')}</span><span className="font-thin opacity-60">{t('tutorial_slides.p8_b1_text')}</span></span> },
      { emoji: "👁️", text: <span><span className="font-thin opacity-60">{t('tutorial_slides.p8_b2_text')}</span><span className="font-bold">{t('tutorial_slides.p8_b2_bold')}</span><span className="font-thin opacity-60">{t('tutorial_slides.p8_b2_post')}</span></span> },
      { emoji: "⚔️", text: <span><span className="font-thin opacity-60">{t('tutorial_slides.p8_b3_text')}</span><span className="font-bold">{t('tutorial_slides.p8_b3_bold')}</span></span> },
      { emoji: "🔄", text: <span><span className="font-bold">{t('tutorial_slides.p8_b4_bold')}</span><span className="font-thin opacity-60">{t('tutorial_slides.p8_b4_text')}</span></span> },
    ],
  },
  {
    title: t('tutorial_slides.p9_title'),
    subtitle: t('tutorial_slides.p9_sub'),
    type: "contact" as const,
  },
];

const PhoneSlide = ({ data, imagesLoaded }: { data: any, imagesLoaded: Record<string, boolean> }) => {
  const { t } = useTranslation();
  const imageLoaded = imagesLoaded[data.image || ""];

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center max-h-full overflow-hidden">
      {/* iPhone Screenshot with fixed aspect ratio container */}
      <motion.div
        initial={{ opacity: 0, y: data.type === "scan-effect" ? -20 : 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="flex justify-center w-full flex-shrink min-h-0"
      >
        <div className="w-[65vw] max-w-[260px] min-[390px]:w-[75vw] min-[390px]:max-w-[340px] min-[480px]:w-[80vw] min-[480px]:max-w-[400px] max-h-[calc(100vh-280px)] relative" style={{ aspectRatio: "9/19.5" }}>
          <img
            src={data.image}
            alt={data.title}
            className="w-full h-full object-contain rounded-3xl"
            style={data.showAiTip ? {
              maskImage: 'linear-gradient(to top, transparent 0%, black 50%, black 100%)',
              WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 50%, black 100%)',
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-out'
            } : {
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-out'
            }}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted-foreground/10 rounded-3xl animate-pulse" />
          )}

          {/* Scanning Effect */}
          {data.type === "scan-effect" && imageLoaded && (
            <>
              <div
                className="absolute inset-0 z-10 will-change-transform"
                style={{
                  maskImage: `url(${data.image})`,
                  WebkitMaskImage: `url(${data.image})`,
                  maskSize: 'contain',
                  WebkitMaskSize: 'contain',
                  maskPosition: 'center',
                  WebkitMaskPosition: 'center',
                  maskRepeat: 'no-repeat',
                  WebkitMaskRepeat: 'no-repeat',
                }}
              >
                <motion.div
                  initial={{ top: "0%" }}
                  animate={{ top: "100%" }}
                  transition={{
                    repeat: Infinity,
                    duration: 4,
                    delay: 0.9,
                    ease: [0.16, 1, 0.3, 1],
                    repeatType: "reverse"
                  }}
                  className="absolute left-0 right-0 h-0.5 bg-primary will-change-[top]"
                  style={{
                    boxShadow: '0 0 15px 2px rgba(34,197,94,0.6)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-primary/0 via-primary/20 to-primary/0 h-32 -translate-y-16" />
                </motion.div>
              </div>
              {/* Floating overlay that appears with scan */}
              {data.overlayImage && imagesLoaded[data.overlayImage] && (
                <motion.div
                  className="absolute inset-0 rounded-3xl pointer-events-none overflow-hidden z-20"
                >
                  <motion.img
                    src={data.overlayImage}
                    alt=""
                    initial={{ clipPath: 'inset(0 0 100% 0)', opacity: 0.9 }}
                    animate={{
                      clipPath: 'inset(0 0 0 0)',
                      opacity: [0.9, 1, 0.9],
                      x: [0, 2, -2, 1, 0],
                      y: [0, -3, 1, -2, 0],
                      rotate: [0, -0.5, 0.5, -0.3, 0],
                    }}
                    transition={{
                      clipPath: {
                        repeat: Infinity,
                        repeatType: "reverse",
                        delay: 0.9,
                        duration: 4,
                        ease: [0.16, 1, 0.3, 1]
                      },
                      opacity: {
                        repeat: Infinity,
                        duration: 2,
                        ease: "easeInOut"
                      },
                      x: {
                        repeat: Infinity,
                        duration: 6,
                        ease: "easeInOut",
                        times: [0, 0.25, 0.5, 0.75, 1]
                      },
                      y: {
                        repeat: Infinity,
                        duration: 7,
                        ease: "easeInOut",
                        times: [0, 0.25, 0.5, 0.75, 1]
                      },
                      rotate: {
                        repeat: Infinity,
                        duration: 8,
                        ease: "easeInOut",
                        times: [0, 0.25, 0.5, 0.75, 1]
                      }
                    }}
                    className="w-full h-full object-contain"
                    style={{
                      filter: 'drop-shadow(0 0 3px rgba(255, 255, 255, 0.3)) drop-shadow(0 0 6px rgba(255, 255, 255, 0.15))'
                    }}
                  />
                </motion.div>
              )}
            </>
          )}

          {/* Fade in overlay for fade-overlay type */}
          {data.type === "fade-overlay" && data.overlayImage && imageLoaded && imagesLoaded[data.overlayImage] && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 1.5, ease: "easeInOut" }}
              className="absolute inset-0 rounded-3xl pointer-events-none overflow-hidden"
            >
              <motion.img
                src={data.overlayImage}
                alt=""
                initial={{ clipPath: 'inset(0 0 100% 0)' }}
                animate={{ clipPath: 'inset(0 0 0 0)' }}
                transition={{ delay: 0.8, duration: 1.5, ease: "easeInOut" }}
                className="w-full h-full object-contain"
              />
            </motion.div>
          )}

          {/* Floating Elements */}
          {data.elements && (
            <AnimatePresence>
              {data.elements.map((el: any, i: number) => {
                const Icon = el.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.5, y: 20 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      y: [0, -10, 0]
                    }}
                    exit={{ opacity: 0, scale: 0.5, y: -20 }}
                    transition={{
                      opacity: { duration: 0.3, delay: (el.delay || 0.75) + (i * 0.2) },
                      scale: { duration: 0.3, delay: (el.delay || 0.75) + (i * 0.2) },
                      y: {
                        repeat: Infinity,
                        duration: 3,
                        ease: "easeInOut",
                        delay: (el.delay || 0.75) + (i * 0.5)
                      }
                    }}
                    className={`absolute ${el.position} bg-card/90 backdrop-blur-sm p-2 rounded-lg border border-border flex items-center gap-2 shadow-2xl z-20`}
                  >
                    <Icon className={`w-3 h-3 min-[390px]:w-4 min-[390px]:h-4 ${el.color}`} />
                    <div className="text-[10px] min-[390px]:text-[11px] min-[480px]:text-xs font-medium text-foreground whitespace-nowrap">
                      {el.text}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </motion.div>

      {/* AI Suggestion Tooltip */}
      {data.showAiTip && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="absolute top-[60%] left-0 right-0 flex justify-center px-4"
        >
          <Card className="p-3 bg-amber-500/10 border-amber-500/30 backdrop-blur-sm w-72">
            <div className="flex gap-2 items-start">
              <Lightbulb className="w-4 h-4 min-[390px]:w-5 min-[390px]:h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs min-[390px]:text-sm text-amber-100 leading-tight">
                  {data.aiTipText}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Text overlaid on image */}
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className={`absolute left-0 right-0 text-center space-y-1 ${data.showIcon === "edit" ? "bottom-[0px]" : "bottom-[4px]"
          }`}
      >
        <p className="text-foreground text-sm max-h-[800px]:text-xs sm:text-base min-[390px]:text-base min-[480px]:text-lg font-medium px-4 flex items-center justify-center gap-2">
          {data.showIcon === "mic" && <Mic className="w-3.5 h-3.5 min-[390px]:w-4 min-[390px]:h-4 min-[480px]:w-5 min-[480px]:h-5" />}
          {data.showIcon === "edit" && <Pencil className="w-3.5 h-3.5 min-[390px]:w-4 min-[390px]:h-4 min-[480px]:w-5 min-[480px]:h-5" />}
          {data.showIcon === "fileEdit" && <FileEdit className="w-3.5 h-3.5 min-[390px]:w-4 min-[390px]:h-4 min-[480px]:w-5 min-[480px]:h-5" />}
          {data.showEmoji && <span className="text-xs min-[390px]:text-sm">{data.showEmoji}</span>}
          {data.title}
        </p>
        {data.subtext === "accuracy-bullets" ? (
          <div className="space-y-1.5 flex flex-col items-center px-4 mb-2">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex items-center gap-2 text-muted-foreground text-xs min-[390px]:text-sm"
            >
              <span>•</span>
              <span>{t('ui.text.less_than_8_calorie_bias')}</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="flex items-center gap-2 text-muted-foreground text-xs min-[390px]:text-sm"
            >
              <span>•</span>
              <span>{t('ui.text.realtime_web_search_usda_food_data')}</span>
            </motion.div>
          </div>
        ) : data.subtext && (
          <p className="text-muted-foreground text-xs min-[390px]:text-sm px-4 mb-2">
            {data.subtext}
          </p>
        )}
      </motion.div>
    </div>
  );
};

const LoopingSequence = ({ sequence, imagesLoaded, onComplete }: { sequence: any[], imagesLoaded: Record<string, boolean>, onComplete?: () => void }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const currentDuration = sequence[index].duration || 4000;
    const timeout = setTimeout(() => {
      setIndex(prev => {
        const next = (prev + 1) % sequence.length;
        if (next === 0 && onComplete) {
          onComplete();
        }
        return next;
      });
    }, currentDuration);
    return () => clearTimeout(timeout);
  }, [index, sequence, onComplete]);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.5 }}
          className="w-full flex items-center justify-center"
        >
          <PhoneSlide data={sequence[index]} imagesLoaded={imagesLoaded} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const OnboardingTutorial = ({
  onComplete,
  isTourFlow = false,
  metricsGenerated = false,
  metricsGenerating = false,
  userInput = "",
  onMetricsGenerated,
  onMetricsError,
  metricsCount = 0
}: OnboardingTutorialProps) => {
  const { t } = useTranslation();
  const tutorialPages = useMemo(() => getTutorialPages(t), [t]);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<Record<string, boolean>>({});
  const [isLocked, setIsLocked] = useState(false);
  const [subtextIndex, setSubtextIndex] = useState(0);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(metricsGenerating);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [hcaptchaToken, setHcaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<any>(null);
  const [captchaRetryCount, setCaptchaRetryCount] = useState(0);
  const [generatedMetricsCount, setGeneratedMetricsCount] = useState(metricsCount);
  const [generatedMetricsEmojis, setGeneratedMetricsEmojis] = useState<string[]>([]);
  const [tutorialPagesCompleted, setTutorialPagesCompleted] = useState(false);
  const [shouldAutoComplete, setShouldAutoComplete] = useState(false);
  const tutorialPagesCompletedRef = useRef(false);

  // Cycling subtexts for page 1 (progress pics)
  const progressPicsSubtexts = [
    <span className="flex items-center justify-center gap-1.5"><Bell className="w-3.5 h-3.5" />{t('tutorial_slides.p6_sub1')}</span>,
    <span className="flex items-center justify-center gap-1.5"><Globe className="w-3.5 h-3.5" />{t('tutorial_slides.p6_sub2')}</span>
  ];

  // Loading phrases that cycle while AI generates metrics
  const loadingPhrases = [
    t('tutorial_slides.analyzing'),
    ...((t('metrics.loading_phrases', { returnObjects: true }) as string[]) || [])
  ];

  const backgroundGradients = [
    "from-cyan-500/30 via-blue-500/15 to-transparent",
    "from-orange-200/20 via-fuchsia-500/10 to-transparent",
    "from-red-600/20 via-white/10 to-transparent",
    "from-emerald-500/20 via-purple-500/10 to-transparent",
    "from-lime-500/20 via-lime-400/10 to-transparent",
    "from-orange-500/20 via-amber-900/10 to-transparent",
    "from-purple-600/20 via-green-500/10 to-transparent",
    "bg-[radial-gradient(ellipse_at_bottom,_rgba(239,68,68,0.2),_rgba(234,179,8,0.2),_rgba(34,197,94,0.2),_rgba(59,130,246,0.2),_rgba(168,85,247,0.2),_transparent_70%)]",
    "from-red-600/20 via-blue-600/10 to-transparent",
    "from-[#5865F2]/20 via-[#5865F2]/10 to-transparent",
    "from-yellow-400/20 via-white/10 to-transparent"
  ];

  // Cycle through loading phrases while generating
  useEffect(() => {
    if (showLoadingOverlay && !loadingComplete) {
      const timer = setTimeout(() => {
        setLoadingPhraseIndex(prev => (prev + 1) % loadingPhrases.length);
      }, 2000); // Change every 2 seconds
      return () => clearTimeout(timer);
    } else {
      setLoadingPhraseIndex(0);
    }
  }, [showLoadingOverlay, loadingComplete, loadingPhraseIndex]);

  // Handle captcha verification
  const handleCaptchaVerify = (token: string) => {
    console.log('[OnboardingTutorial] hCaptcha verified, token received');
    setHcaptchaToken(token);
    generateMetrics(token);
  };

  const handleCaptchaLoad = () => {
    console.log('[OnboardingTutorial] hCaptcha loaded, executing...');
    if (captchaRef.current) {
      const captchaInstance = captchaRef.current;

      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          try {
            if (captchaRef.current && captchaInstance && typeof captchaInstance.execute === 'function') {
              captchaInstance.execute();
            }
          } catch (error) {
            console.error('[OnboardingTutorial] hCaptcha execute error:', error);
          }
        }, { timeout: 100 });
      } else {
        setTimeout(() => {
          try {
            if (captchaRef.current && captchaInstance && typeof captchaInstance.execute === 'function') {
              captchaInstance.execute();
            }
          } catch (error) {
            console.error('[OnboardingTutorial] hCaptcha execute error:', error);
          }
        }, 50);
      }
    }
  };

  // Generate metrics in the background
  const generateMetrics = async (captchaToken?: string) => {
    const token = captchaToken || hcaptchaToken;

    if (!token || !userInput) {
      console.warn('[OnboardingTutorial] Missing token or userInput');
      if (onMetricsError) onMetricsError();
      setShowLoadingOverlay(false);
      return;
    }

    try {
      incrementRateLimit();
      const allMetrics = await getAllMetrics();
      const enabledMetrics = (Array.isArray(allMetrics) ? allMetrics : []).map(m => ({
        key: m.key,
        name: m.name,
        emoji: m.emoji,
        unit: m.unit || "",
      }));

      const deviceId = getDeviceId();

      // Add 3 second delay before calling API to extend animation time
      await new Promise(resolve => setTimeout(resolve, 3000));

      const response = await supabase.functions.invoke("parse-quick-entry", {
        body: {
          text: userInput,
          enabledMetrics,
          anonymous: true,
          device_id: deviceId,
          hcaptcha_token: token,
        },
      });

      if (response.error) {
        console.log("[OnboardingTutorial] Edge function error, navigating to auth signup");
        navigate("/auth?signup=true&from=tour");
        if (onMetricsError) onMetricsError();
        return;
      }

      if (!response.data || !response.data.metrics) {
        console.log("[OnboardingTutorial] No metrics returned, navigating to auth signup");
        navigate("/auth?signup=true&from=tour");
        if (onMetricsError) onMetricsError();
        return;
      }

      // Transform response to PreAuthMetric format
      const usedSlots = new Set<string>();
      const availableSlots = Array.from({ length: 10 }, (_, i) => `custom_${i + 1}`);

      const generatedMetrics: PreAuthMetric[] = response.data.metrics
        .filter((m: any) => m.value !== undefined)
        .map((m: any) => {
          const isBuiltIn = m.key && allMetrics.some(builtin => builtin.key === m.key);

          let metricKey: string;

          if (isBuiltIn) {
            metricKey = m.key;
          } else {
            const nextSlot = availableSlots.find(slot => !usedSlots.has(slot));
            if (nextSlot) {
              metricKey = nextSlot;
              usedSlots.add(nextSlot);
            } else {
              return null;
            }
          }

          return {
            key: metricKey,
            name: m.name,
            emoji: m.emoji || "⭐",
            unit: m.unit || "",
            type: m.type || "numeric",
            goal: m.goal,
            value: m.value || 0,
            inverse: m.inverse || false,
          };
        })
        .filter((m): m is PreAuthMetric => m !== null);

      if (generatedMetrics.length === 0) {
        throw new Error("Couldn't understand that. Try being more specific!");
      }

      setGeneratedMetricsCount(generatedMetrics.length);

      // Extract unique emojis
      const uniqueEmojis = Array.from(new Set(generatedMetrics.map(m => m.emoji)));
      setGeneratedMetricsEmojis(uniqueEmojis);

      setLoadingComplete(true);

      console.log('[OnboardingTutorial] Metrics generated successfully, count:', generatedMetrics.length);
      console.log('[OnboardingTutorial] tutorialPagesCompleted:', tutorialPagesCompleted, 'ref:', tutorialPagesCompletedRef.current);

      // Keep the overlay visible with completion message
      if (onMetricsGenerated) {
        onMetricsGenerated(generatedMetrics);
      }

      // If user already completed tutorial pages, schedule auto-complete
      // Check both state and ref to handle race condition
      if (tutorialPagesCompleted || tutorialPagesCompletedRef.current) {
        console.log('[OnboardingTutorial] Scheduling auto-complete in 2 seconds');
        setTimeout(() => {
          setShouldAutoComplete(true);
        }, 2000); // Show success message for 2 seconds before setting flag
      } else {
        console.log('[OnboardingTutorial] Tutorial not completed yet, waiting for user to finish');
      }
    } catch (err: any) {
      console.error("Error generating metrics:", err);
      console.log("[OnboardingTutorial] Error caught, navigating to auth signup");
      setShowLoadingOverlay(false);
      navigate("/auth?signup=true&from=tour");
      if (onMetricsError) onMetricsError();
    }
  };

  // Start metrics generation when component mounts (if metricsGenerating is true)
  useEffect(() => {
    if (metricsGenerating && userInput && !hcaptchaToken) {
      // Captcha will auto-execute on load
    }
  }, []);

  // Auto-complete when metrics are done and user clicked through tutorial
  useEffect(() => {
    if (shouldAutoComplete && loadingComplete) {
      console.log('[OnboardingTutorial] Auto-completing after metrics generation');
      // Hide overlay before completing
      setShowLoadingOverlay(false);
      // Small delay to ensure state updates
      setTimeout(() => {
        onComplete();
      }, 100);
    }
  }, [shouldAutoComplete, loadingComplete, onComplete]);

  // Cycle through subtext messages on page 5 (progress pics)
  useEffect(() => {
    if (currentPage === 5) {
      const timer = setTimeout(() => {
        setSubtextIndex(prev => (prev + 1) % progressPicsSubtexts.length);
      }, 3500); // Change every 3.5 seconds
      return () => clearTimeout(timer);
    }
  }, [currentPage, subtextIndex]);

  // Reset subtext index when leaving page 5
  useEffect(() => {
    if (currentPage !== 5) {
      setSubtextIndex(0);
    }
  }, [currentPage]);

  // Preload all images
  useEffect(() => {
    const imagesToPreload: string[] = [];

    tutorialPages.forEach(page => {
      if ((page.type === "single-image" || page.type === "scan-effect" || page.type === "wide-image" || page.type === "fade-overlay") && page.image) {
        imagesToPreload.push(page.image);
        if ((page as any).overlayImage) {
          imagesToPreload.push((page as any).overlayImage);
        }
      }
      if ((page as any).type === "looping-sequence" && (page as any).sequence) {
        (page as any).sequence.forEach((step: any) => {
          if (step.image) {
            imagesToPreload.push(step.image);
          }
          if (step.overlayImage) {
            imagesToPreload.push(step.overlayImage);
          }
        });
      }
    });

    imagesToPreload.forEach(src => {
      const img = new Image();
      img.onload = () => {
        setImagesLoaded(prev => ({ ...prev, [src]: true }));
      };
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if ((tutorialPages[currentPage] as any).type === "looping-sequence") {
      setIsLocked(false); // Changed from true to false - allow skipping
    } else {
      setIsLocked(false);
    }
  }, [currentPage]);

  const handleNext = () => {
    // Removed the locking check - allow next button at any time
    // Move to next page
    if (currentPage < tutorialPages.length - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      // Mark that user clicked through all tutorial pages
      console.log('[OnboardingTutorial] Tutorial pages completed, metricsGenerating:', metricsGenerating, 'loadingComplete:', loadingComplete);
      setTutorialPagesCompleted(true);
      tutorialPagesCompletedRef.current = true;

      // If metrics are still generating, show fullscreen loading
      if (metricsGenerating && !loadingComplete) {
        console.log('[OnboardingTutorial] Keeping loading overlay visible');
        // Keep showing the loading overlay fullscreen
        setShowLoadingOverlay(true);
      } else {
        console.log('[OnboardingTutorial] Completing immediately');
        // Otherwise complete normally
        onComplete();
      }
    }
  };

  const currentPageData = tutorialPages[currentPage];

  const renderPageContent = () => {
    if (currentPageData.type === "wide-image") {
      const imageLoaded = imagesLoaded[currentPageData.image || ""];

      return (
        <div className="relative">
          {/* Wide Screenshot for progress snaps */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex justify-center"
          >
            <div className="w-[95vw] max-w-[520px] min-[390px]:max-w-[620px] min-[480px]:max-w-[720px] relative">
              <img
                src={currentPageData.image}
                alt={typeof currentPageData.title === 'string' ? currentPageData.title : "Tutorial image"}
                className={`w-full h-auto object-contain rounded-2xl transition-opacity duration-300 ${imageLoaded ? "opacity-100" : "opacity-0"
                  }`}
              />
              {!imageLoaded && (
                <div className="absolute inset-0 bg-muted-foreground/10 rounded-2xl animate-pulse" />
              )}
            </div>
          </motion.div>

          {/* Text below image */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-6 text-center space-y-2 px-4"
          >
            <p className="text-foreground text-base min-[390px]:text-lg min-[480px]:text-xl font-medium">
              {currentPageData.title}
            </p>
            {currentPage === 5 ? (
              <div className="h-5 relative flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={subtextIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5 }}
                    className="text-muted-foreground text-sm min-[390px]:text-base absolute"
                  >
                    {progressPicsSubtexts[subtextIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>
            ) : currentPageData.subtext === "accuracy-bullets" ? (
              <div className="space-y-1.5 flex flex-col items-center">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="flex items-center gap-2 text-muted-foreground text-sm min-[390px]:text-base"
                >
                  <span>•</span>
                  <span>{t('tutorial_slides.p3_b1')}</span>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="flex items-center gap-2 text-muted-foreground text-sm min-[390px]:text-base"
                >
                  <span>•</span>
                  <span>{t('tutorial_slides.p3_b2')}</span>
                </motion.div>
              </div>
            ) : currentPageData.subtext && (
              <p className="text-muted-foreground text-sm min-[390px]:text-base">
                {currentPageData.subtext}
              </p>
            )}
          </motion.div>
        </div>
      );
    }

    if ((currentPageData as any).type === "looping-sequence") {
      return <LoopingSequence
        sequence={(currentPageData as any).sequence}
        imagesLoaded={imagesLoaded}
        onComplete={() => setIsLocked(false)}
      />;
    }

    if (currentPageData.type === "single-image" || currentPageData.type === "scan-effect" || currentPageData.type === "fade-overlay") {
      return <PhoneSlide data={currentPageData} imagesLoaded={imagesLoaded} />;
    }

    if (currentPageData.type === "bullet-list" && currentPageData.bullets) {
      return (
        <div className="space-y-6 max-h-[800px]:space-y-4">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold text-foreground">
              {currentPageData.title}
            </h2>
          </motion.div>

          {/* Bullets */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-6 px-4"
          >
            {currentPageData.bullets.map((bullet, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="flex items-center gap-4"
              >
                {bullet.emoji && (
                  <span
                    className={`text-3xl min-[390px]:text-4xl min-[480px]:text-5xl ${(bullet as any).redBackground ? 'bg-red-500 px-2 py-1 rounded-lg' : ''}`}
                  >
                    {bullet.emoji}
                  </span>
                )}
                <p className="text-foreground text-base min-[390px]:text-lg">{bullet.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      );
    }

    if (currentPageData.type === "contact") {
      return (
        <div className="space-y-8 max-h-[800px]:space-y-6">
          {/* Title and Subtitle */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-2"
          >
            <h2 className="text-3xl min-[390px]:text-4xl font-bold text-foreground">
              {currentPageData.title}
            </h2>
            {currentPageData.subtitle && (
              <p className="text-muted-foreground text-base min-[390px]:text-lg px-4">
                {currentPageData.subtitle}
              </p>
            )}
          </motion.div>

          {/* Contact Options */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-4 px-6"
          >
            <Button
              onClick={() => openInExternalBrowser("https://discord.gg/FaYD2mJFhW")}
              variant="outline"
              className="w-full h-auto py-4 px-6 border-[#5865F2] text-[#5865F2] hover:bg-[#5865F2]/10"
            >
              <div className="flex items-center gap-3 w-full">
                <MessageCircle className="w-6 h-6 min-[390px]:w-7 min-[390px]:h-7 flex-shrink-0" />
                <div className="flex-1 text-left">
                  <p className="font-semibold text-base min-[390px]:text-lg">{t('ui.text.join_discord')}</p>
                  <p className="text-xs min-[390px]:text-sm opacity-80">{t('ui.text.community_support')}</p>
                </div>
                <ChevronRight className="w-5 h-5 opacity-50" />
              </div>
            </Button>

            <div className="w-full h-auto py-4 px-6 rounded-md border border-border">
              <div className="flex items-center gap-3 w-full">
                <Mail className="w-6 h-6 min-[390px]:w-7 min-[390px]:h-7 flex-shrink-0 text-foreground" />
                <div className="flex-1 text-left">
                  <p className="font-semibold text-base min-[390px]:text-lg text-foreground">{t('ui.text.email_founders')}</p>
                  <p className="text-xs min-[390px]:text-sm text-muted-foreground">{t('ui.text.feedback_requests')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      );
    }

    return null;
  };

  // Calculate total steps for progress dots
  const getTotalSteps = () => {
    return tutorialPages.length;
  };

  const getCurrentStepIndex = () => {
    return currentPage;
  };

  const totalSteps = tutorialPages.length;
  const currentStepIndex = currentPage;
  // Calculate progress percentage
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  return (
    <div className={`fixed inset-0 bg-background flex flex-col items-center overflow-hidden transition-colors duration-700 ${
      // Add subtle tint based on page index
      currentPage % 2 === 0 ? 'bg-background' : 'bg-background/95'
      }`}>
      {/* Invisible hCaptcha for metrics generation */}
      {metricsGenerating && !hcaptchaToken && (
        <SafeHCaptcha
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={handleCaptchaVerify}
          onLoad={handleCaptchaLoad}
          onError={(err) => {
            console.error('[OnboardingTutorial] hCaptcha error:', err);
            // Auto-retry captcha — it must not be skippable
            if (captchaRetryCount < 5) {
              console.log(`[OnboardingTutorial] Retrying captcha (${captchaRetryCount + 1}/5)...`);
              setCaptchaRetryCount(prev => prev + 1);
              setTimeout(() => {
                if (captchaRef.current) {
                  try {
                    if (typeof captchaRef.current.resetCaptcha === 'function') {
                      captchaRef.current.resetCaptcha();
                    }
                    if (typeof captchaRef.current.execute === 'function') {
                      captchaRef.current.execute();
                    }
                  } catch (e) {
                    console.error('[OnboardingTutorial] Captcha re-execute failed:', e);
                  }
                }
              }, 2000);
            } else {
              setShowLoadingOverlay(false);
              if (onMetricsError) onMetricsError();
            }
          }}
          ref={captchaRef}
          size="invisible"
          loadAsync={true}
        />
      )}
      {/* Background Gradients - Reduced intensity */}
      <div
        className={`absolute inset-0 bg-gradient-to-br transition-all duration-1000 ease-in-out opacity-30 ${backgroundGradients[currentPage % backgroundGradients.length]}`}
      />

      {/* Additional ambient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background pointer-events-none" />

      {/* Main Content Area */}
      <div className="flex-1 w-full max-w-md flex flex-col relative z-10 h-full max-h-[100dvh]">
        {/* Content Container - Uses fractional height for better layout on all screns */}
        <div className="flex-1 flex flex-col justify-center min-h-0 relative">
          <AnimatePresence mode="wait" custom={currentPage}>
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="w-full h-full flex flex-col justify-center"
            >
              {renderPageContent()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer Navigation - Fixed height area at bottom */}
        <div className="flex-shrink-0 w-full px-6 pb-8 pt-4 space-y-4 bg-gradient-to-t from-background via-background/90 to-transparent">
          {/* Progress Indicators */}
          <div className="flex justify-center gap-1.5 mb-2">
            {tutorialPages.map((_, index) => (
              <motion.div
                key={index}
                className={`h-1 rounded-full transition-all duration-300 ${index === currentStepIndex
                  ? "w-6 bg-primary"
                  : index < currentStepIndex
                    ? "w-2 bg-primary/50"
                    : "w-2 bg-muted-foreground/30"
                  }`}
              />
            ))}
          </div>

          {/* Next button */}
          <motion.div
            className="px-2 pb-2 pt-4"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 1 }}
          >
            <Button
              onClick={handleNext}
              className={`w-full text-base min-[390px]:text-lg font-semibold rounded-full transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-primary to-primary/80 shadow-[0_0_30px_-5px_hsl(var(--primary)/0.6)] hover:shadow-[0_0_30px_-5px_hsl(var(--primary)/0.8)] ${currentStepIndex < totalSteps - 1 ? "h-12 min-[390px]:h-14" : "h-auto py-3 min-[390px]:py-4"
                } relative overflow-hidden`}
              size="lg"
            >
              <div className="relative z-10 flex items-center justify-center w-full">
                {currentStepIndex < totalSteps - 1 ? (
                  <>
                    {t('tutorial_slides.next')}
                    <ChevronRight className="w-5 h-5 min-[390px]:w-6 min-[390px]:h-6 ml-2" />
                  </>
                ) : (
                  <div className="flex items-center justify-center w-full">
                    <span>
                      {loadingComplete && generatedMetricsCount > 0
                        ? t('tutorial_slides.view_metrics')
                        : metricsGenerated
                          ? t('tutorial_slides.continue_signup')
                          : isTourFlow
                            ? t('tutorial_slides.start_tracking')
                            : t('tutorial_slides.get_started_free')}
                    </span>
                    <ChevronRight className="w-6 h-6 min-[390px]:w-7 min-[390px]:h-7 ml-2 flex-shrink-0" />
                  </div>
                )}
              </div>
            </Button>
          </motion.div>
          {currentStepIndex < totalSteps - 1 && (
            <motion.button
              onClick={() => {
                // Jump to last page and trigger its logic
                setCurrentPage(totalSteps - 1);
                // Trigger the same logic as the last button
                setTutorialPagesCompleted(true);
                tutorialPagesCompletedRef.current = true;

                if (metricsGenerating && !loadingComplete) {
                  setShowLoadingOverlay(true);
                } else {
                  onComplete();
                }
              }}
              className="w-full text-xs text-primary/60 hover:text-primary transition-colors flex items-center justify-center gap-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 1 }}
            >
              {t('tutorial_slides.skip_tour')}
            </motion.button>
          )}
        </div>
      </div>

      {/* Loading Overlay - Animates from fullscreen center to top center (or stays fullscreen if tutorial completed) */}
      <AnimatePresence>
        {showLoadingOverlay && (
          <>
            {/* Black background that fades out (or stays if tutorial completed) */}
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: tutorialPagesCompleted ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, delay: tutorialPagesCompleted ? 0 : 2 }}
              className="fixed inset-0 bg-black z-50 pointer-events-none"
            />

            {/* Loading spinner and text */}
            <motion.div
              initial={{
                position: "fixed",
                top: "50%",
                left: "50%",
                x: "-50%",
                y: "-50%",
              }}
              animate={tutorialPagesCompleted ? {
                // Stay centered if tutorial was completed
                position: "fixed",
                top: "50%",
                left: "50%",
                x: "-50%",
                y: "-50%",
              } : {
                // Otherwise move to top
                position: "fixed",
                top: "20px",
                left: "50%",
                x: "-50%",
                y: "0%",
              }}
              transition={{
                duration: 0.8,
                delay: tutorialPagesCompleted ? 0 : 2,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="z-50 pointer-events-none"
            >
              <motion.div
                className="flex items-center justify-center"
                initial={{ flexDirection: 'column', gap: '1rem' }}
                animate={{ flexDirection: 'column', gap: tutorialPagesCompleted ? '1rem' : '0.5rem' }}
                transition={{ duration: 0.5, delay: tutorialPagesCompleted ? 0 : 1 }}
              >
                {/* Glowing spinner - hide when complete, shrink at transition */}
                <AnimatePresence>
                  {!loadingComplete && (
                    <motion.div
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="relative flex-shrink-0"
                      initial={{ width: 80, height: 80 }}
                      animate={{ width: tutorialPagesCompleted ? 80 : 24, height: tutorialPagesCompleted ? 80 : 24 }}
                      transition={{ duration: 0.5, delay: tutorialPagesCompleted ? 0 : 1 }}
                    >
                      {/* Outer glow ring */}
                      <motion.div
                        className="absolute inset-0 rounded-full bg-primary/30 blur-md"
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.5, 0.8, 0.5],
                        }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      />

                      {/* Spinning ring */}
                      <motion.div
                        className="absolute inset-0.5 rounded-full border border-primary/40 border-t-primary"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                      />

                      {/* Center icon */}
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center"
                        initial={{ scale: 1 }}
                        animate={{ scale: tutorialPagesCompleted ? 1 : 0.3 }}
                        transition={{ duration: 0.5, delay: tutorialPagesCompleted ? 0 : 1 }}
                      >
                        <Sparkles className="w-8 h-8 text-primary" />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Text content */}
                <AnimatePresence mode="wait">
                  {!loadingComplete ? (
                    <div className="relative flex flex-col items-center">
                      <motion.p
                        initial={{ opacity: 1, height: 'auto', marginTop: 0, marginBottom: 0 }}
                        animate={{
                          opacity: tutorialPagesCompleted ? 1 : 0,
                          height: tutorialPagesCompleted ? 'auto' : 0,
                          marginTop: 0,
                          marginBottom: 0
                        }}
                        transition={{ duration: 0.5, delay: tutorialPagesCompleted ? 0 : 1 }}
                        className="text-xl font-bold text-foreground whitespace-nowrap text-center overflow-hidden"
                      >
                        {t('tutorial_slides.generating_metrics')}
                      </motion.p>
                      <motion.div
                        className="h-5 flex items-center justify-center"
                        initial={{ marginTop: '0px' }}
                        animate={{ marginTop: '0px' }}
                        transition={{ duration: 0.5, delay: tutorialPagesCompleted ? 0 : 1 }}
                      >
                        <AnimatePresence mode="wait">
                          <motion.p
                            key={loadingPhraseIndex}
                            initial={{ opacity: 0, y: 10, fontSize: tutorialPagesCompleted ? '1rem' : '1rem' }}
                            animate={{ opacity: 1, y: 0, fontSize: tutorialPagesCompleted ? '1rem' : '0.875rem' }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.4 }}
                            className="text-muted-foreground whitespace-nowrap text-center font-light"
                          >
                            {loadingPhrases[loadingPhraseIndex]}
                          </motion.p>
                        </AnimatePresence>
                      </motion.div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-4">
                      <motion.p
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 0.6, y: 0 }}
                        transition={{
                          delay: 2.5,
                          duration: 0.6,
                          ease: "easeOut"
                        }}
                        className="text-xs text-muted-foreground/60 whitespace-nowrap text-center mb-4"
                      >
                        {t('tutorial_slides.metrics_waiting', { count: generatedMetricsCount })}
                      </motion.p>
                      {generatedMetricsEmojis.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 1, duration: 0.4 }}
                          className="flex items-center justify-center gap-1.5 text-xl"
                        >
                          {generatedMetricsEmojis.map((emoji, idx) => (
                            <motion.span
                              key={idx}
                              initial={{ opacity: 0, scale: 0 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 1 + (idx * 0.1), duration: 0.3, type: "spring" }}
                            >
                              {emoji}
                            </motion.span>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

